import { useCallback, useMemo, useState } from "react";

import { Alert } from "../../../../ui/alert";
import { Checkbox } from "../../../../ui/checkbox";
import { Label } from "../../../../ui/label";
import { Spinner } from "../../../../ui/spinner";

import { getEnvironmentProps } from "../../../../../utils/environment";

import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";

import {
  apiFetchNotOkHandler,
  errorHandler
} from "../../../../../APIs/errorHandler";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { useShowAICoPilot } from "../../../../../hooks/useShowAICoPilot";
import { EDSButton } from "../../../../eds/EDSButton";
import EDSTabs from "../../../../eds/EDSTabs";
import {
  OptimizationStrategy,
  ProcessedScanResult,
  ScanConfiguration
} from "../SpaceScanner.types";
import { CoPilotChat } from "../AIInsights/CoPilotChat";

/**
 * Scan Type
 */
type ScanType = {
  id: string;
  label: string;
};

/**
 * Scan Types
 */
const scanTypes: ScanType[] = [
  {
    id: "all-seated",
    label: "All seated"
  },
  {
    id: "seated-and-standing",
    label: "Seated and standing"
  }
];

/**
 * Space Scanner Stage 1 Props
 */
type SpaceScannerStage1Props = {
  onResult: (result: ProcessedScanResult[], config: ScanConfiguration) => void;
  onClose: () => void;
};

/**
 * Configure and perform scan (Stage 1)
 * @param param0
 * @returns
 */
export const SpaceScannerStage1 = ({ onResult }: SpaceScannerStage1Props) => {
  const { connectionStatus } = useDeviceContext();
  const { date, venueConfig } = useDiaryContext();
  const showAICoPilot = useShowAICoPilot();
  const [selectedScanType, setSelectedScanType] = useState<ScanType>(
    scanTypes[0]
  );
  const [isGoOverCapacitySelected, setIsGoOverCapacitySelected] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [optimizationStrategy, setOptimizationStrategy] =
    useState<OptimizationStrategy>("maximize_capacity");

  /**
   * Perform scan, optionally using config overrides from the AI Assistant.
   * When overrides are provided the scan uses those values directly,
   * avoiding stale-state issues from React's asynchronous updates.
   */
  const performScan = useCallback(
    async (configOverride?: Partial<ScanConfiguration>) => {
      if (connectionStatus === "none") {
        setIsLoading(false);
        setError(
          "An internet connection is required to use the Space Optimiser tool. Please check your internet connection and try again."
        );

        return;
      }

      setIsLoading(true);

      const token = await dmnIdentity.getAccessToken();
      const environmentVars = getEnvironmentProps(token);

      const allowOverCapacity =
        configOverride?.allowOverCapacity ?? !!isGoOverCapacitySelected;
      const dateString = date.toISOString().split("T")[0];
      const searchSeatedCapacity =
        configOverride?.searchSeatedCapacity ??
        selectedScanType.id === "seated-and-standing";
      const strategy =
        configOverride?.optimizationStrategy ?? optimizationStrategy;
      const includeExplanation = showAICoPilot;
      const areasToEmpty = showAICoPilot
        ? (configOverride?.areasToEmpty ?? [])
        : [];
      const strategyParam =
        showAICoPilot && strategy !== "balanced" ? `&strategy=${strategy}` : "";
      const areasToEmptyParam =
        showAICoPilot && areasToEmpty.length > 0
          ? `&areasToEmpty=${encodeURIComponent(areasToEmpty.join(","))}`
          : "";

      try {
        const result = await fetch(
          `${environmentVars?.CORE_API_BASE_URL}/venues/${venueConfig?._id}/optimise-booking-areas?allowOverCapacity=${allowOverCapacity}&date=${dateString}&searchSeatedCapacity=${searchSeatedCapacity}${includeExplanation ? "&includeExplanation=true" : ""}${strategyParam}${areasToEmptyParam}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!result.ok) {
          setIsLoading(false);
          setError("Something went wrong. Please try again.");
          apiFetchNotOkHandler(result, "Error performing scan");

          return;
        }

        const data = (await result.json()).payload.results;
        setIsLoading(false);

        const config: ScanConfiguration = {
          allowOverCapacity,
          searchSeatedCapacity,
          ...(showAICoPilot && {
            optimizationStrategy: strategy,
            areasToEmpty: areasToEmpty.length > 0 ? areasToEmpty : undefined
          })
        };
        onResult(data, config);
      } catch (err) {
        setIsLoading(false);
        setError("Something went wrong. Please try again.");
        errorHandler(err, "Error performing scan");
      }
    },
    [
      isGoOverCapacitySelected,
      date,
      selectedScanType,
      connectionStatus,
      optimizationStrategy,
      showAICoPilot,
      onResult,
      venueConfig
    ]
  );

  /**
   * Handle AI Assistant apply: update local state for UI consistency,
   * then trigger a scan with the explicit config values.
   */
  const handleApplyAndScan = useCallback(
    (update: Partial<ScanConfiguration>) => {
      if (update.allowOverCapacity !== undefined) {
        setIsGoOverCapacitySelected(update.allowOverCapacity);
      }
      if (update.searchSeatedCapacity !== undefined) {
        const newType = update.searchSeatedCapacity
          ? scanTypes[1]
          : scanTypes[0];
        setSelectedScanType(newType);
      }
      if (update.optimizationStrategy) {
        setOptimizationStrategy(update.optimizationStrategy);
      }
      performScan(update);
    },
    [performScan]
  );

  const handleScanResultsReady = useCallback(
    (results: ProcessedScanResult[], config: ScanConfiguration) => {
      onResult(results, config);
    },
    [onResult]
  );

  const currentConfig = useMemo<ScanConfiguration>(
    () => ({
      allowOverCapacity: isGoOverCapacitySelected,
      searchSeatedCapacity: selectedScanType.id === "seated-and-standing",
      optimizationStrategy: optimizationStrategy
    }),
    [isGoOverCapacitySelected, selectedScanType.id, optimizationStrategy]
  );

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <p className="flex-shrink-0 text-sm leading-relaxed text-muted-foreground line-clamp-2 mb-4">
        Live-check your diary to make sure you are utilising your venue space to
        its most efficient capacity. Scan bookings and tables for an optimised
        seating plan.
      </p>

      <div className="overflow-y-auto flex-1 min-h-0">
        {error && <Alert variant="destructive">{error}</Alert>}
        {isLoading && (
          <div className="flex justify-center p-4">
            <Spinner className="size-8" />
          </div>
        )}
        {!isLoading && !error && (
          <div className="flex flex-row flex-wrap content-center justify-evenly items-center">
            <div className="py-2 flex flex-col flex-wrap content-center justify-center items-center">
              <EDSTabs
                tabs={scanTypes}
                selectedTab={selectedScanType.id}
                onTabChange={(tabId) => {
                  const newScanType = scanTypes.find((t) => t.id === tabId);
                  if (newScanType) {
                    setSelectedScanType(newScanType);
                  }
                }}
              />
            </div>
            <div className="py-2 flex flex-col flex-wrap content-center justify-center items-center">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="overCapacity"
                  checked={isGoOverCapacitySelected}
                  onCheckedChange={(checked) => {
                    setIsGoOverCapacitySelected(checked as boolean);
                  }}
                  className="border h-6 w-6 border-solid border-[hsla(180,70%,45%,0.2)]"
                />
                <Label htmlFor="overCapacity" className="font-bold">
                  Go over capacity if no area found?
                </Label>
              </div>
            </div>
          </div>
        )}
        {!isLoading && !error && showAICoPilot && (
          <CoPilotChat
            onApplyAndScan={handleApplyAndScan}
            onScanResultsReady={handleScanResultsReady}
            currentConfig={currentConfig}
          />
        )}
      </div>
      <div className="flex-shrink-0 pt-3 sm:pt-[var(--panelPadding)]">
        <EDSButton
          className="w-full"
          disabled={isLoading || !!error}
          onClick={() => {
            performScan();
          }}
        >
          {isLoading ? "Scanning..." : "Scan areas"}
        </EDSButton>
      </div>
    </div>
  );
};
